import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeft, ChevronRight, Save, ShieldCheck,
  Clock, Calendar, Zap, Users,
  CalendarOff, CalendarCheck, Trash2, Plus, Minus
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const D_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const D_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const getFirstDay = (y, m) => new Date(y, m, 1).getDay();
const toKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const toDateKey = toKey;
const isToday = (y, m, d) => {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
};
const isPast = (y, m, d) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(y, m, d) < t;
};
const fmtTime = t => {
  if (!t) return '';
  const [h, mi] = t.split(':');
  let hr = parseInt(h);
  const ap = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12 || 12;
  return `${hr}:${mi} ${ap}`;
};
const timeToDecimal = t => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
};
const defaultSlot = () => ({ startTime: '09:00', endTime: '17:00', maxSurgeries: 5 });

// ── Mini Calendar ─────────────────────────────────────────────────────────────
// selectedDates = Set of "YYYY-MM-DD" strings for individually selected dates
// offDays = Set of "YYYY-MM-DD" strings marked as day-off
function MiniCalendar({ selectedDates, offDays = new Set(), slotMap = {}, onDateClick, isDateDisabled }) {
  const todayObj = new Date();
  const [cur, setCur] = useState({ y: todayObj.getFullYear(), m: todayObj.getMonth() });

  const prev = () => setCur(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const next = () => setCur(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });

  const totalDays = getDaysInMonth(cur.y, cur.m);
  const firstDay = getFirstDay(cur.y, cur.m);
  const cells = Array(firstDay).fill(null).concat(
    Array.from({ length: totalDays }, (_, i) => i + 1)
  );

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-lg overflow-hidden">
      {/* Month nav */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#143E78] to-[#1a52a0]">
        <button onClick={prev} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
          <ChevronLeft size={16} />
        </button>
        <span className="text-white font-black text-sm tracking-widest uppercase">
          {MONTHS[cur.m]} {cur.y}
        </span>
        <button onClick={next} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-50">
        {D_SHORT.map(d => (
          <div key={d} className="py-2 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Cells — each cell is ONE specific date, not a weekday */}
      <div className="grid grid-cols-7 p-3 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const past = isPast(cur.y, cur.m, day);
          const todayCell = isToday(cur.y, cur.m, day);
          const dateKey = toDateKey(cur.y, cur.m, day);
          const isDisabled = past || (isDateDisabled && isDateDisabled(dateKey));
          const selected = selectedDates.has(dateKey);
          const isOff = offDays.has(dateKey);
          const slot = slotMap[dateKey];
          const dow = new Date(cur.y, cur.m, day).getDay();

          return (
            <button
              key={dateKey}
              onClick={() => !isDisabled && onDateClick(dateKey)}
              disabled={isDisabled}
              title={selected && slot
                ? `${D_FULL[dow]} ${day}: ${fmtTime(slot.startTime)} – ${fmtTime(slot.endTime)}`
                : `${D_FULL[dow]}, ${day} ${MONTHS[cur.m]}`}
              className={`
                relative aspect-square flex flex-col items-center justify-center rounded-xl text-xs font-bold transition-all
                ${isDisabled ? 'opacity-55 cursor-not-allowed bg-slate-100 text-slate-500 font-medium' : 'cursor-pointer hover:scale-105'}
                ${todayCell ? 'ring-2 ring-[#143E78] ring-offset-1 border-2 border-[#143E78] font-black' : ''}
                ${selected && isOff && !isDisabled
                  ? 'bg-amber-100 text-amber-800 border-2 border-amber-300 shadow-sm'
                  : selected && !isDisabled
                    ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-200'
                    : !isDisabled
                      ? 'bg-slate-200/80 text-slate-700 hover:bg-blue-100 hover:text-[#143E78]'
                      : ''}
              `}
            >
              <span>{day}</span>
              {selected && !isDisabled && (
                <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isOff ? 'bg-amber-400' : 'bg-white/70'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 pb-4 pt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500" />
          <span className="text-[10px] font-bold text-gray-400">Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-amber-100 border border-amber-200" />
          <span className="text-[10px] font-bold text-gray-400">Day Off</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-200" />
          <span className="text-[10px] font-bold text-gray-400">Unselected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-[#143E78]" />
          <span className="text-[10px] font-bold text-gray-400">Today</span>
        </div>
      </div>
    </div>
  );
}

// ── Day Row ───────────────────────────────────────────────────────────────────
function DayRow({ day, isActive, slot, onToggle, onChange, highlight }) {
  const abbr = day.charAt(0).toUpperCase() + day.slice(1, 3).toUpperCase();
  return (
    <div className={`
      rounded-2xl border-2 transition-all duration-300 overflow-hidden
      ${isActive
        ? highlight
          ? 'border-blue-400 shadow-lg shadow-blue-100 bg-white'
          : 'border-blue-100 shadow-sm bg-white'
        : 'border-gray-100 bg-gray-50/60'}
      ${highlight ? 'ring-2 ring-blue-300 ring-offset-1' : ''}
    `}>
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Day badge */}
        <div className={`
          w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 font-black text-xs transition-all
          ${isActive
            ? 'bg-gradient-to-br from-[#143E78] to-[#1a52a0] text-white shadow-md'
            : 'bg-gray-100 text-gray-400'}
        `}>
          <span className="text-[10px] uppercase tracking-wider opacity-70">{abbr.slice(0, 3)}</span>
        </div>

        {/* Toggle */}
        <div className="flex items-center gap-3 min-w-[130px]">
          <button
            onClick={onToggle}
            className={`relative w-11 h-6 rounded-full transition-colors duration-300 focus:outline-none ${isActive ? 'bg-[#143E78]' : 'bg-gray-200'}`}
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${isActive ? 'left-6' : 'left-1'}`} />
          </button>
          <span className={`font-black text-sm tracking-tight ${isActive ? 'text-[#143E78]' : 'text-gray-400'}`}>
            {day.charAt(0).toUpperCase() + day.slice(1)}
          </span>
        </div>

        {/* Inputs */}
        {isActive ? (
          <div className="flex-1 grid grid-cols-3 gap-3">
            <div>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Start</p>
              <input type="time" value={slot.startTime}
                onChange={e => onChange('startTime', e.target.value)}
                className="w-full bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-sm font-bold text-[#143E78] focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">End</p>
              <input type="time" value={slot.endTime}
                onChange={e => onChange('endTime', e.target.value)}
                className="w-full bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-sm font-bold text-[#143E78] focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Max Cases</p>
              <input type="number" min="1" max="20" value={slot.maxSurgeries}
                onChange={e => onChange('maxSurgeries', parseInt(e.target.value) || 1)}
                className="w-full bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-sm font-bold text-[#143E78] focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <p className="text-sm text-gray-400 italic font-medium">No surgeries scheduled</p>
          </div>
        )}

        {/* Status pill */}
        <div className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
          }`}>
          {isActive ? 'Active' : 'Off'}
        </div>
      </div>

      {/* Time bar */}
      {isActive && (
        <div className="px-5 pb-3">
          <div className="h-1.5 bg-blue-50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#143E78] to-blue-400 rounded-full transition-all duration-500"
              style={{
                marginLeft: `${(parseInt(slot.startTime) / 24) * 100}%`,
                width: `${((parseInt(slot.endTime) - parseInt(slot.startTime)) / 24) * 100}%`
              }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[9px] font-bold text-blue-400">{fmtTime(slot.startTime)}</span>
            <span className="text-[9px] font-bold text-blue-400">{fmtTime(slot.endTime)}</span>
          </div>
        </div>
      )}
    </div>
  );
}



function WeeklyDayCard({ dayPattern, onChange }) {
  const { day, isAvailable, startTime, endTime } = dayPattern;

  return (
    <div className={`p-6 rounded-[30px] border transition-all duration-300 ${
      isAvailable 
        ? 'border-blue-100 bg-white shadow-sm' 
        : 'border-slate-100 bg-slate-50/50'
    }`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Toggle & Day Label */}
        <div className="flex items-center gap-4 min-w-[200px]">
          <button
            type="button"
            onClick={() => onChange(day, 'isAvailable', !isAvailable)}
            className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none ${
              isAvailable ? 'bg-[#143E78]' : 'bg-slate-200'
            }`}
          >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
              isAvailable ? 'left-8' : 'left-1'
            }`} />
          </button>
          <span className={`font-black text-lg tracking-tight ${
            isAvailable ? 'text-[#143E78]' : 'text-slate-400 font-bold'
          }`}>
            {day}
          </span>
        </div>

        {/* Inputs or Off-duty message */}
        {isAvailable ? (
          <div className="flex-1 grid grid-cols-2 gap-4 max-w-lg">
            {/* Start Time */}
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                <Clock size={10} className="text-slate-400" /> Start Time
              </p>
              <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-350 focus-within:ring-2 focus-within:ring-[#143E78]/10 focus-within:border-[#143E78] transition-all overflow-hidden">
                <input 
                  type="time" 
                  value={startTime}
                  onChange={e => onChange(day, 'startTime', e.target.value)}
                  className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                />
                <div className="absolute right-3 pointer-events-none text-slate-400">
                  <Clock size={14} />
                </div>
              </div>
            </div>

            {/* End Time */}
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                <Clock size={10} className="text-slate-400" /> End Time
              </p>
              <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-350 focus-within:ring-2 focus-within:ring-[#143E78]/10 focus-within:border-[#143E78] transition-all overflow-hidden">
                <input 
                  type="time" 
                  value={endTime}
                  onChange={e => onChange(day, 'endTime', e.target.value)}
                  className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                />
                <div className="absolute right-3 pointer-events-none text-slate-400">
                  <Clock size={14} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 py-2 flex items-center">
            <p className="text-sm text-slate-400 italic font-medium">Off-duty. No surgeries will be assigned for this day.</p>
          </div>
        )}
      </div>
    </div>
  );
}



// ── Date Slot Card ────────────────────────────────────────────────────────────
function DateSlotCard({ dateKey, slot, onRemove, onChange, onToggleOff, isFirst, isOff }) {
  const d = new Date(dateKey + 'T00:00:00');
  const dow = d.getDay();
  const day = d.getDate();
  const mon = MONTHS[d.getMonth()].slice(0, 3);
  const yr = d.getFullYear();

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-md
      ${isOff
        ? 'border-slate-100 bg-slate-50/40 opacity-75'
        : isFirst
          ? 'border-indigo-400/40 shadow-md shadow-indigo-100/30 bg-white ring-1 ring-indigo-500/5'
          : 'border-slate-100 shadow-sm bg-white'}`}>

      <style>{`
        .custom-time-input::-webkit-calendar-picker-indicator {
          background: transparent;
          bottom: 0;
          color: transparent;
          cursor: pointer;
          height: auto;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: auto;
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-5">
        {/* Date badge */}
        <div className={`w-14 h-15 rounded-xl border flex flex-col overflow-hidden items-center justify-center flex-shrink-0 transition-all duration-300 shadow-sm
          ${isOff
            ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-60'
            : isFirst
              ? 'bg-white border-indigo-200 ring-2 ring-indigo-500/10'
              : 'bg-white border-slate-200'}`}
        >
          <div className={`w-full py-0.5 text-center text-[8px] font-black uppercase tracking-wider
            ${isOff
              ? 'bg-slate-200 text-slate-500'
              : isFirst
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white'
                : 'bg-[#143E78] text-white'}`}
          >
            {mon}
          </div>
          <div className={`flex-1 flex items-center justify-center w-full font-black text-lg py-1
            ${isOff ? 'text-slate-400' : 'text-[#143E78]'}`}
          >
            {day}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <p className={`font-black text-sm tracking-tight ${isOff ? 'text-slate-400' : 'text-slate-800'}`}>
              {D_FULL[dow]}, {day} {MONTHS[d.getMonth()]} {yr}
            </p>
            {isFirst && !isOff && (
              <span className="text-[9px] font-extrabold bg-indigo-50 border border-indigo-150 text-indigo-600 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Start Date
              </span>
            )}
            {isOff && (
              <span className="text-[9px] font-extrabold bg-slate-100 border border-slate-200 text-slate-400 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Day Off
              </span>
            )}
          </div>

          {isOff ? (
            <p className="text-xs text-slate-400 italic font-medium">No surgeries scheduled for this day.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Start Time Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Clock size={10} className="text-slate-400" /> Start Time
                </p>
                <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all overflow-hidden">
                  <input
                    type="time"
                    value={slot.startTime}
                    onChange={e => onChange('startTime', e.target.value)}
                    className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                  />
                  <div className="absolute right-3 pointer-events-none text-slate-400">
                    <Clock size={14} />
                  </div>
                </div>
              </div>

              {/* End Time Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Clock size={10} className="text-slate-400" /> End Time
                </p>
                <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all overflow-hidden">
                  <input
                    type="time"
                    value={slot.endTime}
                    onChange={e => onChange('endTime', e.target.value)}
                    className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                  />
                  <div className="absolute right-3 pointer-events-none text-slate-400">
                    <Clock size={14} />
                  </div>
                </div>
              </div>

              {/* Max Cases Stepper Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Users size={10} className="text-slate-400" /> Max Cases
                </p>
                <div className="flex items-center border border-slate-200 bg-slate-50 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 hover:border-slate-300 transition-all h-[38px]">
                  <button
                    type="button"
                    disabled={slot.maxSurgeries <= 1}
                    onClick={() => onChange('maxSurgeries', Math.max(1, slot.maxSurgeries - 1))}
                    className="px-3 h-full flex items-center justify-center text-slate-500 hover:bg-slate-200/50 active:bg-slate-200 transition-all font-bold disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Minus size={12} />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={slot.maxSurgeries}
                    onChange={e => onChange('maxSurgeries', Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    className="w-full text-center bg-transparent border-0 text-slate-700 font-bold text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    disabled={slot.maxSurgeries >= 20}
                    onClick={() => onChange('maxSurgeries', Math.min(20, slot.maxSurgeries + 1))}
                    className="px-3 h-full flex items-center justify-center text-slate-500 hover:bg-slate-200/50 active:bg-slate-200 transition-all font-bold disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Row */}
        <div className="flex flex-row sm:flex-col items-center gap-2 flex-shrink-0 sm:self-center">
          {/* Toggle off/on */}
          <button
            onClick={onToggleOff}
            title={isOff ? 'Mark as available' : 'Mark as day off'}
            className={`px-3 py-2 rounded-xl text-xs font-black transition-all border flex items-center gap-1.5 shadow-sm active:scale-95 w-full justify-center
              ${isOff
                ? 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700'
                : 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-700'}`}
          >
            {isOff ? (
              <>
                <CalendarCheck size={14} />
                <span>Activate</span>
              </>
            ) : (
              <>
                <CalendarOff size={14} />
                <span>Day Off</span>
              </>
            )}
          </button>

          {/* Remove from range entirely */}
          <button
            onClick={onRemove}
            title="Remove from range"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-650 active:scale-95 shadow-sm transition-all sm:w-full sm:h-9"
          >
            <Trash2 size={14} className="mr-0" />
            <span className="hidden sm:hidden text-xs font-black">Remove</span>
          </button>
        </div>
      </div>

      {/* Time bar — only when active */}
      {!isOff && (() => {
        const startDec = timeToDecimal(slot.startTime);
        const endDec = timeToDecimal(slot.endTime);
        const duration = Math.max(0, endDec - startDec);

        const leftPct = (startDec / 24) * 100;
        const widthPct = (duration / 24) * 100;

        return (
          <div className="px-6 pb-4 pt-2 border-t border-slate-100 bg-slate-50/20">
            {/* 24h Visual Grid */}
            <div className="relative h-4 mt-2">
              <div className="absolute inset-0 h-1.5 bg-slate-100 rounded-full top-1/2 -translate-y-1/2" />

              <div className="absolute left-[25%] top-1/2 -translate-y-1/2 w-[1px] h-2 bg-slate-300" />
              <div className="absolute left-[50%] top-1/2 -translate-y-1/2 w-[1px] h-3 bg-slate-400" />
              <div className="absolute left-[75%] top-1/2 -translate-y-1/2 w-[1px] h-2 bg-slate-300" />

              <div
                className="absolute h-2.5 rounded-full bg-gradient-to-r from-indigo-600 via-indigo-500 to-cyan-500 shadow-md shadow-indigo-500/20 top-1/2 -translate-y-1/2 transition-all duration-500"
                style={{
                  left: `${leftPct}%`,
                  width: `${widthPct}%`
                }}
              >
                <div className="absolute -left-1 -top-0.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-indigo-600 shadow-sm" />
                <div className="absolute -right-1 -top-0.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-cyan-500 shadow-sm" />
              </div>
            </div>

            {/* Grid labels */}
            <div className="relative flex justify-between text-[9px] font-black tracking-wider text-slate-300 uppercase select-none mt-1">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>12 PM</span>
              <span>6 PM</span>
              <span>12 AM</span>
            </div>

            {/* Floating details */}
            <div className="flex justify-between items-center mt-3 pt-2.5 border-t border-dashed border-slate-100">
              <span className="text-[10px] font-bold text-slate-400">Scheduled Operating Window</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-extrabold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-lg shadow-sm">
                  {fmtTime(slot.startTime)}
                </span>
                <span className="text-slate-300 font-bold text-[10px]">to</span>
                <span className="text-[11px] font-extrabold text-cyan-600 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded-lg shadow-sm">
                  {fmtTime(slot.endTime)}
                </span>
                <span className="text-slate-300 font-bold ml-1 text-[10px]">•</span>
                <span className="text-[11px] font-extrabold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg shadow-sm ml-1">
                  {duration.toFixed(1).replace('.0', '')} hrs
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const DoctorAvailability = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);

  const getActiveWeekBounds = () => {
    const dates = Object.keys(dateSlots || {}).map(d => new Date(d + 'T00:00:00'));
    let maxDate = null;
    if (dates.length > 0) {
      maxDate = new Date(Math.max(...dates));
      maxDate.setHours(0, 0, 0, 0);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startOfWeek = new Date();

    if (maxDate && today <= maxDate) {
      // Lock active week to the Sunday of the week containing maxDate
      startOfWeek = new Date(maxDate);
      startOfWeek.setDate(maxDate.getDate() - maxDate.getDay());
    } else if (maxDate && today > maxDate) {
      // The chosen dates have passed, shift to next Sunday after maxDate
      const maxDateSunday = new Date(maxDate);
      maxDateSunday.setDate(maxDate.getDate() - maxDate.getDay());
      startOfWeek = new Date(maxDateSunday);
      startOfWeek.setDate(maxDateSunday.getDate() + 7);
    } else if (availability?.completedAt) {
      const anchor = new Date(availability.completedAt);
      startOfWeek = new Date(anchor);
      startOfWeek.setDate(anchor.getDate() - anchor.getDay()); // Sunday
    } else {
      // Standard dynamic fallback
      const day = today.getDay();
      startOfWeek = new Date(today);
      if (day >= 3) {
        startOfWeek.setDate(today.getDate() + (7 - day));
      } else {
        startOfWeek.setDate(today.getDate() - day);
      }
    }

    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
  };

  const isDateDisabled = (dateKey) => {
    const cellDate = new Date(dateKey + 'T00:00:00');
    const { startOfWeek, endOfWeek } = getActiveWeekBounds();
    return cellDate < startOfWeek || cellDate > endOfWeek;
  };

  const getDateKeyForDay = (dayName) => {
    const { startOfWeek } = getActiveWeekBounds();
    const dow = D_FULL.findIndex(f => f.toLowerCase() === dayName.toLowerCase());
    if (dow === -1) return null;
    const targetDate = new Date(startOfWeek);
    targetDate.setDate(startOfWeek.getDate() + dow);
    return toDateKey(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  };

  // dateSlots: { [YYYY-MM-DD]: { startTime, endTime, maxSurgeries } }
  const [dateSlots, setDateSlots] = useState({});

  // offDays: Set of "YYYY-MM-DD" keys the doctor has marked as day-off within the range
  const [offDays, setOffDays] = useState(new Set());

  // selectedDates for the calendar = all range keys (including off days shown as grey)
  const selectedDates = new Set(Object.keys(dateSlots));

  const [activeTab, setActiveTab] = useState('schedule');

  const [weeklyPattern, setWeeklyPattern] = useState([
    { day: 'Monday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Tuesday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Wednesday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Thursday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Friday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Saturday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Sunday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
  ]);

  const handleWeeklyPatternChange = (dayName, field, value) => {
    // 1. Update weekly pattern
    setWeeklyPattern(prev => prev.map(p => {
      if (p.day === dayName) {
        return { ...p, [field]: value };
      }
      return p;
    }));

    // 2. Synchronize with calendar date slots
    const dateKey = getDateKeyForDay(dayName);
    if (!dateKey) return;

    if (field === 'isAvailable') {
      if (value) {
        // Toggle on: Add to dateSlots, remove from offDays
        const pattern = weeklyPattern.find(p => p.day === dayName);
        setDateSlots(prev => ({
          ...prev,
          [dateKey]: {
            startTime: pattern?.startTime || '09:00',
            endTime: pattern?.endTime || '17:00',
            maxSurgeries: 5
          }
        }));
        setOffDays(prev => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      } else {
        // Toggle off: Remove from dateSlots and offDays
        setDateSlots(prev => {
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
        setOffDays(prev => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      }
    } else if (field === 'startTime' || field === 'endTime') {
      // Update time slots if that day is currently active in dateSlots
      setDateSlots(prev => {
        if (!prev[dateKey]) return prev;
        return {
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            [field]: value
          }
        };
      });
    }
  };

  const loadWeeklyPattern = (dbSchedule) => {
    if (!dbSchedule || !dbSchedule.length) return;
    const mapped = D_FULL.map(day => {
      const dbDay = dbSchedule.find(d => d.day.toLowerCase() === day.toLowerCase());
      if (dbDay) {
        return {
          day,
          isAvailable: dbDay.isAvailable,
          startTime: dbDay.timeSlots?.[0]?.startTime || '09:00',
          endTime: dbDay.timeSlots?.[0]?.endTime || '17:00'
        };
      }
      return { day, isAvailable: false, startTime: '09:00', endTime: '17:00' };
    });
    setWeeklyPattern(mapped);
  };

  const buildWeeklyPatternPayload = () => {
    return weeklyPattern.map(p => ({
      day: p.day,
      isAvailable: p.isAvailable,
      timeSlots: p.isAvailable ? [{ startTime: p.startTime, endTime: p.endTime, maxSurgeries: 5 }] : []
    }));
  };

  const getDayNameFromDateKey = (dateKey) => {
    const d = new Date(dateKey + 'T00:00:00');
    return D_FULL[d.getDay()];
  };

  const syncWeeklyPatternForDate = (dateKey, isAvailable, startTime = null, endTime = null) => {
    const dayName = getDayNameFromDateKey(dateKey);
    setWeeklyPattern(prev => prev.map(p => {
      if (p.day.toLowerCase() === dayName.toLowerCase()) {
        return {
          ...p,
          isAvailable,
          ...(startTime ? { startTime } : {}),
          ...(endTime ? { endTime } : {})
        };
      }
      return p;
    }));
  };

  // Calendar click behaviour: 3-way toggle (Unselected -> Active -> Day Off -> Unselected)
  const handleDateClick = (dateKey) => {
    const isSelected = selectedDates.has(dateKey);
    const isOff = offDays.has(dateKey);

    if (!isSelected) {
      // Unselected -> Active (green)
      if (selectedDates.size >= 7) {
        toast.error("Maximum 7 days allowed. Remove a date first.", { icon: '🚫', duration: 3000 });
        return;
      }
      setDateSlots(prev => ({ ...prev, [dateKey]: defaultSlot() }));
      setOffDays(prev => { const next = new Set(prev); next.delete(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, true);
    } else if (!isOff) {
      // Active (green) -> Day Off (amber)
      setOffDays(prev => { const next = new Set(prev); next.add(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, false);
    } else {
      // Day Off (amber) -> Unselected (white)
      setDateSlots(prev => { const next = { ...prev }; delete next[dateKey]; return next; });
      setOffDays(prev => { const next = new Set(prev); next.delete(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, false);
    }
  };

  // Toggle a day within the range as off/on (keeps it in the list)
  const handleToggleOff = (dateKey) => {
    const isCurrentlyOff = offDays.has(dateKey);
    setOffDays(prev => {
      const next = new Set(prev);
      if (isCurrentlyOff) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
    syncWeeklyPatternForDate(dateKey, isCurrentlyOff);
  };

  const handleSlotChange = (dateKey, field, value) => {
    setDateSlots(prev => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], [field]: value }
    }));
    if (field === 'startTime' || field === 'endTime') {
      syncWeeklyPatternForDate(dateKey, true, field === 'startTime' ? value : null, field === 'endTime' ? value : null);
    }
  };

  const handleRemoveDate = (dateKey) => {
    setDateSlots(prev => {
      const next = { ...prev };
      delete next[dateKey];
      return next;
    });
    setOffDays(prev => {
      const next = new Set(prev);
      next.delete(dateKey);
      return next;
    });
    syncWeeklyPatternForDate(dateKey, false);
  };

  const sortedDateKeys = Object.keys(dateSlots).sort();
  const activeDateKeys = sortedDateKeys.filter(k => !offDays.has(k));

  const totalHours = activeDateKeys.reduce((acc, k) => {
    const s = dateSlots[k];
    return acc + Math.max(0, timeToDecimal(s.endTime) - timeToDecimal(s.startTime));
  }, 0);
  const totalCases = activeDateKeys.reduce((acc, k) => acc + (dateSlots[k]?.maxSurgeries || 0), 0);

  useEffect(() => { fetchAvailability(); }, []);

  const fetchAvailability = async () => {
    try {
      const ep = id ? `/doctor-availability/${id}` : '/doctor-availability/me';
      const res = await api.get(ep);
      const data = res.data.data;
      setAvailability(data);
      if (data?.weeklySchedule) {
        loadWeeklyPattern(data.weeklySchedule);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const loaded = {};
        data.weeklySchedule.forEach(d => {
          if (!d.isAvailable || !d.timeSlots?.length) return;
          const dowTarget = D_FULL.findIndex(f => f.toLowerCase() === d.day.toLowerCase());
          if (dowTarget === -1) return;
          const diff = (dowTarget - today.getDay() + 7) % 7 || 7;
          const date = new Date(today); date.setDate(today.getDate() + diff);
          const key = toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
          loaded[key] = {
            startTime: d.timeSlots[0].startTime || '09:00',
            endTime: d.timeSlots[0].endTime || '17:00',
            maxSurgeries: d.timeSlots[0].maxSurgeries || 5
          };
        });
        if (Object.keys(loaded).length) {
          setDateSlots(loaded);
        }
      }
    } catch { toast.error('Failed to load availability'); }
    finally { setLoading(false); }
  };

  const buildSchedule = () => {
    const byDay = {};
    Object.entries(dateSlots).forEach(([key, slot]) => {
      if (offDays.has(key)) return; // skip off days
      const dayName = D_FULL[new Date(key + 'T00:00:00').getDay()];
      if (!byDay[dayName]) byDay[dayName] = slot;
    });
    return D_FULL.map(day => ({
      day,
      isAvailable: !!byDay[day],
      timeSlots: byDay[day] ? [byDay[day]] : []
    }));
  };



  const handleSave = async () => {
    const isWeekly = activeTab === 'schedule';
    if (!isWeekly && sortedDateKeys.length === 0) {
      return toast.error('Please select at least one date on the calendar');
    }

    setSaving(true);
    try {
      const schedule = isWeekly ? buildWeeklyPatternPayload() : buildSchedule();
      
      let expiryDate = new Date();
      if (!isWeekly && sortedDateKeys.length > 0) {
        const maxDateStr = sortedDateKeys[sortedDateKeys.length - 1];
        const maxDate = new Date(maxDateStr + 'T00:00:00');
        // Set expiry to the Saturday of the week containing maxDate
        const endOfWeek = new Date(maxDate);
        endOfWeek.setDate(maxDate.getDate() + (6 - maxDate.getDay()));
        endOfWeek.setHours(23, 59, 59, 999);
        expiryDate = endOfWeek;
      } else {
        const { endOfWeek } = getActiveWeekBounds();
        expiryDate = new Date(endOfWeek);
      }

      const ep = id ? `/doctor-availability/${id}` : '/doctor-availability/me';
      await api.put(ep, { weeklySchedule: schedule, expiryDate });
      toast.success('Availability saved successfully!');
      if (id) navigate(-1); else fetchAvailability();
    } catch { 
      toast.error('Failed to save availability'); 
    } finally { 
      setSaving(false); 
    }
  };

  // const totalHours = sortedDateKeys.reduce((acc, k) => {
  //   const s = dateSlots[k];
  //   return acc + Math.max(0, parseInt(s.endTime) - parseInt(s.startTime));
  // }, 0);

  // const totalCases = sortedDateKeys.reduce((acc, k) => acc + (dateSlots[k]?.maxSurgeries || 0), 0);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
      <div className="w-12 h-12 rounded-full border-4 border-[#143E78] border-t-transparent animate-spin" />
      <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Loading Schedule…</p>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto pb-24 space-y-6 font-sans">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={14} className="text-blue-500" />
            <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Verified Surgeon Dashboard</span>
          </div>
          <h1 className="text-3xl font-black text-[#143E78] tracking-tighter leading-tight">
            Manage your upcoming 7 days <span className="text-blue-500">Availability Roaster</span>
          </h1>
          <p className="text-gray-400 font-medium text-sm mt-1">Set your weekly operating windows and surgical capacity.</p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#143E78] hover:bg-[#0f2c56] text-white font-black text-xs shadow-lg shadow-blue-900/20 transition-all hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save Schedule'}
          </button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Selected Days', value: `${activeDateKeys.length} / ${sortedDateKeys.length}`, sub: 'active of total', icon: Calendar, color: 'blue' },
          { label: 'Total Hours', value: `${totalHours.toFixed(1).replace('.0', '')}h`, sub: 'operating time', icon: Clock, color: 'emerald' },
          { label: 'Max Cases', value: totalCases, sub: 'across all dates', icon: Zap, color: 'violet' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
              ${color === 'blue' ? 'bg-blue-50 text-blue-600'
                : color === 'emerald' ? 'bg-emerald-50 text-emerald-600'
                  : 'bg-violet-50 text-violet-600'}`}>
              <Icon size={18} />
            </div>
            <div>
              <p className="text-xl font-black text-gray-900 leading-none">{value}</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-0.5">{label}</p>
              <p className="text-[9px] text-gray-300 font-medium">{sub}</p>
            </div>
          </div>
        ))}
      </div>



      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-fit">
        {[
          { key: 'schedule', label: 'Weekly Schedule', icon: Calendar },
          { key: 'upcoming', label: 'Upcoming Slots', icon: Clock },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-black transition-all ${activeTab === key
                ? 'bg-white text-[#143E78] shadow-sm'
                : 'text-gray-400 hover:text-gray-600'
              }`}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Schedule Tab: Monday to Sunday Weekly Pattern ── */}
      {activeTab === 'schedule' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: default weekly hours */}
          <div className="lg:col-span-2 space-y-4">
            <div className="space-y-4">
              {weeklyPattern.map((p) => (
                <WeeklyDayCard
                  key={p.day}
                  dayPattern={p}
                  onChange={handleWeeklyPatternChange}
                />
              ))}
            </div>
          </div>

          {/* Right: Calendar + info */}
          <div className="space-y-4">
            <MiniCalendar
              selectedDates={selectedDates}
              offDays={offDays}
              onDateClick={handleDateClick}
              isDateDisabled={isDateDisabled}
            />
            <div className="rounded-2xl p-4 border bg-blue-50 border-blue-100">
              <p className="text-[10px] font-black uppercase tracking-widest mb-3 text-blue-600">
                Weekly Overrides
              </p>
              <p className="text-[11px] font-medium leading-relaxed text-blue-700">
                Use the calendar to set specific date overrides for upcoming days. Tap a selected day to set a temporary Day Off.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Upcoming Tab: Interactive Calendar Dates ── */}
      {activeTab === 'upcoming' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: date slot cards */}
          <div className="lg:col-span-2 space-y-4">
            {sortedDateKeys.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
                <Calendar size={36} className="text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-bold text-sm">No dates selected yet</p>
                <p className="text-gray-300 text-xs mt-1">Tap dates on the calendar to select them</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedDateKeys.map((dk, i) => (
                  <DateSlotCard
                    key={dk}
                    dateKey={dk}
                    slot={dateSlots[dk]}
                    isFirst={i === 0}
                    isOff={offDays.has(dk)}
                    onToggleOff={() => handleToggleOff(dk)}
                    onRemove={() => handleRemoveDate(dk)}
                    onChange={(field, val) => handleSlotChange(dk, field, val)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right: Calendar + tips */}
          <div className="space-y-4">
            <MiniCalendar
              selectedDates={selectedDates}
              offDays={offDays}
              onDateClick={handleDateClick}
              isDateDisabled={isDateDisabled}
            />
            <div className="rounded-2xl p-4 border bg-blue-50 border-blue-100">
              <p className="text-[10px] font-black uppercase tracking-widest mb-3 text-blue-600">
                How it works
              </p>
              <ul className="space-y-2">
                {[
                  'Tap any future date on the calendar to select it (green)',
                  'Tap a green date again to mark it as a Day Off (amber)',
                  'Tap an amber date again to remove it from selection',
                  'Maximum of 7 days can be selected at any time',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center flex-shrink-0 mt-0.5 bg-blue-200 text-blue-700">{i + 1}</span>
                    <span className="text-[11px] font-medium leading-tight text-blue-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ── Summary Strip ── */}
      <div className="bg-gradient-to-r from-[#143E78] to-[#1a52a0] rounded-3xl p-6 text-white shadow-2xl shadow-blue-900/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <p className="font-black text-sm uppercase tracking-widest opacity-70">Selected Dates Overview</p>
            <span className="text-[10px] font-black bg-white/10 border border-white/20 px-3 py-1 rounded-full uppercase tracking-wider">
              {sortedDateKeys.length} Date{sortedDateKeys.length !== 1 ? 's' : ''} Selected
            </span>
          </div>
          {sortedDateKeys.length === 0 ? (
            <p className="text-white/40 text-sm font-medium italic">No dates selected yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sortedDateKeys.map((dk, i) => {
                const d = new Date(dk + 'T00:00:00');
                const slot = dateSlots[dk];
                return (
                  <div key={dk} className={`rounded-2xl px-4 py-3 text-center min-w-[80px] transition-all
                    ${i === 0 ? 'bg-white/20 border border-white/30' : 'bg-white/10 border border-white/15'}`}>
                    <p className="text-[9px] font-black uppercase tracking-wider text-blue-200 mb-0.5">{D_SHORT[d.getDay()]}</p>
                    <p className="text-sm font-black leading-none">{d.getDate()} {MONTHS[d.getMonth()].slice(0, 3)}</p>
                    <p className="text-[8px] font-bold opacity-60 mt-1">{fmtTime(slot.startTime)}</p>
                    <p className="text-[8px] font-bold opacity-60">{fmtTime(slot.endTime)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default DoctorAvailability;
